import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Button from "../../../components/button/Button";
import IconButton from "../../../components/button/IconButton";
import Input from "../../../components/form-item/Input";
import Textarea from "../../../components/form-item/Textarea";
import ContentTagInput from "../../../components/content-tag/ContentTagInput";
import DropdownMenu, {
  type DropdownMenuItem,
} from "../../../components/dropdown-menu/DropdownMenu";
import MediaPreview from "../../../components/media-preview/MediaPreview";
import MediaTranscriptionModal from "../../../components/media-preview/components/MediaTranscriptionModal";
import TranscriptionStatusCell from "../../../components/transcription/TranscriptionStatusCell";
import { isTranscribableFileType } from "../../../components/transcription/transcriptionTargets";
import useTranscriptionStatuses, {
  hasTranscriptionPreview,
} from "../../../hooks/useTranscriptionStatuses";
import Modal from "../../../components/modal/Modal";
import Tab from "../../../components/tab/Tab";
import TagSelector from "../../../components/tag-selector/TagSelector";
import FileUploader from "../../../components/file-uploader/FileUploader";
import UploadProgressBar from "../../../components/upload-progress/UploadProgressBar";
import AiImageGeneratorModal from "../../../components/ai-image-generator/AiImageGeneratorModal";
import ToolTip from "../../../components/tooltip/ToolTip";
import type { GeneratedImageCandidate } from "../../../app/services/aiImage";
import {
  useCreateProfileWorkMutation,
  useUpdateProfileWorkMutation,
} from "../../../app/services/creatorProfile";
import useS3Upload, { type UploadingFile } from "../../../hooks/useS3Upload";
import type {
  ProfileMedia,
  ProfileMediaType,
  ProfileWork,
  WorkFilterTag,
  WorkInput,
} from "../../../types/profile";
import {
  detectProfileMediaType,
  normalizeTagList,
  profileMediaIcon,
} from "../../../utils/profileMedia";

interface WorkEditModalProps {
  /** 既存作品の編集なら作品データ、新規追加なら null。 */
  work: ProfileWork | null;
  /**
   * 作品フィルタータグのマスタ（`ProfileContent.work_filter_tags`）。
   * マスタの追加・削除は作品登録ページのバーで行い、ここでは選択のみ。
   */
  filterTags?: WorkFilterTag[];
  onClose: () => void;
  /** 保存に成功した直後の追加処理（モーダル閉じは内部で行う）。 */
  onSaved?: () => void;
}

const MAX_IMAGE_SIZE = 20 * 1024 * 1024;
const MAX_MEDIA_SIZE = 500 * 1024 * 1024;

const MEDIA_ACCEPT = {
  "audio/*": [],
  "video/*": [],
  "image/*": [],
  "application/pdf": [],
} as const;
const MEDIA_ACCEPT_STR = ["audio/*", "video/*", "image/*", "application/pdf"];

const TAB_MEDIA = "メディアファイル";
const TAB_YOUTUBE = "YouTube リンク";

// =====================================================================
// Styled
// =====================================================================

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLabel = styled.label.attrs({
  className: "bodytext-13 u-line-height-100 u-pl4",
})``;

// 既存メディアの 1 枚表示カード（3 点メニューで差し替え / 文字起こし / 削除）。
const CurrentMediaCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: var(--soremo-background);
`;

// =====================================================================
// 本体
// =====================================================================

type ContentSource = "media" | "youtube";

/**
 * 作品の編集モーダル（1 ファイル＝1 作品）。
 *
 * - 作品コンテンツは「メディアファイル」または「YouTube リンク」の排他選択。
 *   既存に YouTube が入っていれば youtube タブで開く。
 * - メディアタブは 1 ファイルのみ。アップロードすると既存メディアを「差し替え」
 *   する仕様（複数を持つレガシー作品はまとめてカード表示し、ファイルを置けば
 *   全て差し替えられる）。
 * - 並び順はショット編集と同じ流儀：メディア/YouTube → 作品名 → フィルタータグ
 *   → 説明 → タグ（GALLERY 検索用ハッシュタグ）→ サムネ画像。
 * - 制作年 / 種別 / クレジットは利用頻度が低いため入力欄を置かない
 *   （payload にも含めず、既存値はサーバー側でそのまま保持される）。
 */
const WorkEditModal: React.FC<WorkEditModalProps> = ({
  work,
  filterTags = [],
  onClose,
  onSaved,
}) => {
  const isNew = work === null;

  // 既存に YouTube が入っていればそちらをデフォルト。
  const initialSource: ContentSource =
    (work?.youtube_link?.trim() ?? "") ? "youtube" : "media";
  const [contentSource, setContentSource] =
    useState<ContentSource>(initialSource);

  const [title, setTitle] = useState(work?.title ?? "");
  const [desc, setDesc] = useState(work?.desc ?? "");
  // 字幕テキスト（「[M:SS] テキスト」行形式。音源 / 動画のみ編集 UI を出す）
  const [subtitleText, setSubtitleText] = useState(work?.subtitle_text ?? "");
  const [youtube, setYoutube] = useState(work?.youtube_link ?? "");
  const [tags, setTags] = useState<string[]>(
    normalizeTagList(work?.tags ?? []),
  );
  // フィルタータグの割当（マスタに実在する id のみ保持）。
  const [filterTagIds, setFilterTagIds] = useState<string[]>(() => {
    const valid = new Set(filterTags.map((t) => t.id));
    return (work?.filter_tag_ids ?? []).filter((id) => valid.has(id));
  });

  // サムネ: null=変更なし / ''=削除 / 文字列=新規アップロード済み file_key
  const [pendingImageKey, setPendingImageKey] = useState<string | null>(null);
  const [localImagePreviewUrl, setLocalImagePreviewUrl] = useState<
    string | null
  >(null);
  const [showAiImageModal, setShowAiImageModal] = useState(false);
  // 既存メディア（音源 / 動画）の文字起こしモーダル
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);

  // 既存メディアの「保持」状態。差し替えボタン押下 or 新規アップロード成功時に false。
  const [keepExistingMedia, setKeepExistingMedia] = useState(true);
  const existingMedia: ProfileMedia[] = work?.media ?? [];
  const hasExistingMedia = existingMedia.length > 0;

  // 代表メディアの文字起こしステータス（実行中はポーリングでチップが切り替わる）。
  // 画像・PDF 作品は対象外なので購読しない。
  const transcriptionFileId =
    existingMedia[0]?.id != null &&
    isTranscribableFileType(existingMedia[0].type)
      ? String(existingMedia[0].id)
      : null;
  const transcriptionFileIds = useMemo(
    () => (transcriptionFileId ? [transcriptionFileId] : []),
    [transcriptionFileId],
  );
  const transcriptionStatuses = useTranscriptionStatuses(transcriptionFileIds);

  // 新規アップロード結果: file_key（1 ファイルのみ）。
  const [pendingMediaKey, setPendingMediaKey] = useState<string | null>(null);
  const [pendingMediaMeta, setPendingMediaMeta] = useState<{
    name: string;
    type: ProfileMediaType;
  } | null>(null);
  const [mediaSelectedFiles, setMediaSelectedFiles] = useState<File[]>([]);

  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const mediaReplaceInputRef = useRef<HTMLInputElement | null>(null);

  const [createWork, { isLoading: isCreating }] =
    useCreateProfileWorkMutation();
  const [updateWork, { isLoading: isUpdating }] =
    useUpdateProfileWorkMutation();

  // 画像（サムネ）専用アップロード
  const { uploadFiles: uploadImage, isUploading: isUploadingImage } =
    useS3Upload({
      purpose: "work_media",
      skipRegister: true,
      restrictions: {
        accept: ["image/*"],
        maxSize: MAX_IMAGE_SIZE,
        maxFiles: 1,
      },
      onValidationError: (errors) =>
        errors.forEach((e) => toast.error(e.message)),
    });

  // メディア（音源/動画/画像/PDF）専用アップロード
  const {
    uploadFiles: uploadMedia,
    uploadingFiles: mediaUploadingFiles,
    isUploading: isUploadingMedia,
    abortFile: abortMediaFile,
    clearCompleted: clearMediaCompleted,
    retryFile: retryMediaFile,
  } = useS3Upload({
    purpose: "work_media",
    skipRegister: true,
    restrictions: {
      accept: MEDIA_ACCEPT_STR,
      maxSize: MAX_MEDIA_SIZE,
      maxFiles: 1,
    },
    onValidationError: (errors) =>
      errors.forEach((e) => toast.error(e.message)),
  });

  const isSaving = isCreating || isUpdating;
  const isBusy = isSaving || isUploadingImage || isUploadingMedia;

  // サムネ表示 URL（新規アップ済み > 削除 > 既存）。
  const displayImageUrl = useMemo(() => {
    if (pendingImageKey === "") return null;
    if (localImagePreviewUrl) return localImagePreviewUrl;
    return work?.image_url ?? null;
  }, [pendingImageKey, localImagePreviewUrl, work?.image_url]);

  // ===== サムネ画像 =====
  const handlePickImage = useCallback(
    async (file: File) => {
      const [result] = await uploadImage([file]);
      if (result?.status === "completed" && result.fileKey) {
        setPendingImageKey(result.fileKey);
        setLocalImagePreviewUrl(URL.createObjectURL(file));
      } else if (result?.status === "error") {
        toast.error("画像のアップロードに失敗しました");
      }
    },
    [uploadImage],
  );

  const handleImageInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handlePickImage(file);
      e.target.value = "";
    },
    [handlePickImage],
  );

  const handleClearImage = useCallback(() => {
    setPendingImageKey("");
    setLocalImagePreviewUrl(null);
  }, []);

  // AI 生成画像を選択: 手動アップロードと同じく pending 状態にセットし、保存時に image_key で送る
  const handleAiImageSelected = useCallback(
    (candidate: GeneratedImageCandidate) => {
      setPendingImageKey(candidate.file_key);
      setLocalImagePreviewUrl(candidate.url);
      setShowAiImageModal(false);
    },
    [],
  );

  // ===== メディア（1 ファイル: 差し替え） =====
  // アップロード完了メディアを差し替え予定としてセットする（初回・再試行共通の後処理）
  const applyUploadedMedia = useCallback((result: UploadingFile) => {
    if (!result.fileKey) return;
    setPendingMediaKey(result.fileKey);
    setPendingMediaMeta({
      name: result.file.name,
      type: detectProfileMediaType(result.file),
    });
    setKeepExistingMedia(false);
  }, []);

  // 失敗行は進捗バーに残り、行の再試行ボタン（handleMediaRetry）で再開できる。
  const handleMediaUpload = useCallback(
    async (file: File) => {
      try {
        const [result] = await uploadMedia([file]);
        if (result?.status === "completed") {
          applyUploadedMedia(result);
        } else if (result?.status === "error") {
          toast.error(`${file.name}: アップロードに失敗しました。再試行できます`);
        }
      } finally {
        setMediaSelectedFiles((prev) => prev.filter((f) => f !== file));
      }
    },
    [uploadMedia, applyUploadedMedia],
  );

  // エラー行の再試行。成功時は通常アップロードと同じく差し替え予定をセットする
  const handleMediaRetry = useCallback(
    async (file: UploadingFile) => {
      const result = await retryMediaFile(file.id);
      if (result?.status === "completed") {
        applyUploadedMedia(result);
      }
    },
    [retryMediaFile, applyUploadedMedia],
  );

  // メディア完了表示は少し見せてからクリーンアップ
  useEffect(() => {
    if (
      !isUploadingMedia &&
      mediaUploadingFiles.length > 0 &&
      mediaUploadingFiles.every((f) => f.status === "completed")
    ) {
      const t = window.setTimeout(() => clearMediaCompleted(), 1500);
      return () => window.clearTimeout(t);
    }
  }, [isUploadingMedia, mediaUploadingFiles, clearMediaCompleted]);

  // 「差し替え」ボタン: アバターの「画像を変更」と同じく、隠し input をクリックして
  // ファイル選択ダイアログを直接開く。選択完了時に `handleMediaUpload` が走り、
  // 差し替え予定カードが表示される。
  const handleReplaceClick = useCallback(() => {
    mediaReplaceInputRef.current?.click();
  }, []);

  const handleMediaReplaceInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleMediaUpload(file);
      e.target.value = "";
    },
    [handleMediaUpload],
  );

  const handleRemoveAllMedia = useCallback(() => {
    setKeepExistingMedia(false);
    setPendingMediaKey(null);
    setPendingMediaMeta(null);
  }, []);

  const handleCancelReplace = useCallback(() => {
    if (!hasExistingMedia) return;
    setKeepExistingMedia(true);
    setPendingMediaKey(null);
    setPendingMediaMeta(null);
    setMediaSelectedFiles([]);
  }, [hasExistingMedia]);

  // グローバル Tab コンポーネント用のラベル <-> ContentSource 変換。
  // タブを切り替えてもローカルの編集状態は保持する（戻ったときに元の状態を
  // 取り戻せる）。排他保存は `buildPayload` が選択中タブの値だけ送ることで担保する。
  const activeTabLabel = contentSource === "youtube" ? TAB_YOUTUBE : TAB_MEDIA;
  const handleTabLabelChange = useCallback((label: string) => {
    setContentSource(label === TAB_YOUTUBE ? "youtube" : "media");
  }, []);

  // 現在有効なメディアの種類（差し替え予定 > 既存保持）。字幕 UI の表示判定に使う。
  const effectiveMediaType: ProfileMediaType | null = pendingMediaMeta
    ? pendingMediaMeta.type
    : keepExistingMedia && hasExistingMedia
      ? existingMedia[0].type
      : null;
  const showSubtitleField =
    contentSource === "media" &&
    (effectiveMediaType === "audio" || effectiveMediaType === "video");

  // ===== 保存 =====
  const buildPayload = useCallback((): WorkInput => {
    // 制作年 / 種別 / クレジットは編集 UI を持たないため payload に含めない
    // （更新 API は部分更新なので既存値は保持される）。
    const payload: WorkInput = {
      title: title.trim(),
      desc: desc.trim(),
      // 排他: YouTube タブのときだけ値を送り、メディアタブのときは空文字でクリア。
      sale_youtube_link: contentSource === "youtube" ? youtube.trim() : "",
      tags,
      filter_tag_ids: filterTagIds,
    };
    if (pendingImageKey !== null) payload.image_key = pendingImageKey;

    if (contentSource === "media") {
      // 既存を保持なら何も触らない。差し替え/削除なら audio_keys を「全置換」で送る。
      if (!keepExistingMedia) {
        payload.audio_keys = pendingMediaKey ? [pendingMediaKey] : [];
      }
      // 字幕はローカル再生（鑑賞モード）専用のため、メディアタブの値をそのまま送る。
      payload.subtitle_text = subtitleText.trim();
    } else {
      // YouTube タブに切り替えたなら、メディアは全削除（明示的に全置換）。
      payload.audio_keys = [];
      // ローカルメディアが無くなるため字幕もクリアする。
      payload.subtitle_text = "";
    }
    return payload;
  }, [
    title,
    desc,
    youtube,
    tags,
    filterTagIds,
    pendingImageKey,
    contentSource,
    keepExistingMedia,
    pendingMediaKey,
    subtitleText,
  ]);

  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      toast.error("作品名は必須です");
      return;
    }
    // YouTube タブでリンク未入力の場合は OK 自体を disabled にしているため、
    // ここでは media タブのみ検証する。
    if (contentSource === "media") {
      const hasAnyMedia =
        (keepExistingMedia && hasExistingMedia) || pendingMediaKey !== null;
      if (!hasAnyMedia) {
        toast.error(
          "メディアファイルか YouTube リンクのどちらかを設定してください",
        );
        return;
      }
    }

    const payload = buildPayload();
    try {
      if (work) {
        await updateWork({ id: work.id, body: payload }).unwrap();
      } else {
        await createWork(payload).unwrap();
      }
      // 成功トーストは出さない（モーダルが閉じること自体が完了のフィードバック）。
      onSaved?.();
      onClose();
    } catch {
      toast.error("保存に失敗しました");
    }
  }, [
    title,
    contentSource,
    keepExistingMedia,
    hasExistingMedia,
    pendingMediaKey,
    buildPayload,
    work,
    updateWork,
    createWork,
    onSaved,
    onClose,
  ]);

  // 既存メディアの代表 1 枚（差し替え UI 用）。複数あればまとめて表示する。
  // 操作は 3 点リーダーに集約: ファイル差し替え / 文字起こし（音源・動画のみ）/ 削除
  const renderCurrentMedia = () => {
    if (!keepExistingMedia || !hasExistingMedia) return null;
    const primary = existingMedia[0];
    const canTranscribe =
      primary.id != null && (primary.type === "audio" || primary.type === "video");
    const menuItems: DropdownMenuItem[] = [
      {
        label: "ファイル差し替え",
        icon: "swap_horiz",
        onClick: handleReplaceClick,
        disabled: isBusy,
      },
      ...(canTranscribe
        ? [
            {
              label: "文字起こし",
              icon: "speech_to_text",
              onClick: () => setShowTranscriptionModal(true),
            },
          ]
        : []),
      {
        label: "削除",
        icon: "delete",
        onClick: handleRemoveAllMedia,
        disabled: isBusy,
      },
    ];
    const transcriptionEntry = transcriptionFileId
      ? transcriptionStatuses[transcriptionFileId]
      : undefined;
    return (
      <Section>
        <CurrentMediaCard>
          <span
            className="material-symbols-rounded u-text-light-gray"
            style={{ fontSize: 24 }}
            aria-hidden
          >
            {profileMediaIcon(primary.type)}
          </span>
          <div className="u-col" style={{ flex: 1, minWidth: 0 }}>
            <div className="u-row u-gap4 u-items-center">
              <ToolTip description={primary.file_name}>
                <span
                  className="bodytext-13"
                  style={{
                    // プレビューがあるときは名前を内容幅に留め、直後から本文を続ける
                    flex: hasTranscriptionPreview(transcriptionEntry)
                      ? "0 1 auto"
                      : 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {primary.file_name}
                </span>
              </ToolTip>
              <TranscriptionStatusCell entry={transcriptionEntry} />
            </div>
            {existingMedia.length > 1 && (
              <span className="bodytext-11 u-text-light-gray">
                ほか {existingMedia.length - 1}{" "}
                件のメディアあり（差し替えで全て置き換わります）
              </span>
            )}
          </div>
          <DropdownMenu items={menuItems} />
          <input
            ref={mediaReplaceInputRef}
            type="file"
            accept={MEDIA_ACCEPT_STR.join(",")}
            onChange={handleMediaReplaceInputChange}
            style={{ display: "none" }}
          />
        </CurrentMediaCard>
      </Section>
    );
  };

  // 新規アップロード後の確認カード（差し替え予定）。
  const renderPendingMedia = () => {
    if (!pendingMediaMeta || !pendingMediaKey) return null;
    return (
      <CurrentMediaCard>
        <span
          className="material-symbols-rounded u-text-light-gray"
          style={{ fontSize: 24 }}
          aria-hidden
        >
          {profileMediaIcon(pendingMediaMeta.type)}
        </span>
        <div className="u-col" style={{ flex: 1, minWidth: 0 }}>
          <ToolTip description={pendingMediaMeta.name}>
            <span
              className="bodytext-13"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {pendingMediaMeta.name}
            </span>
          </ToolTip>
          <span className="bodytext-11 u-text-light-gray">
            保存すると新しいメディアに差し替えます
          </span>
        </div>
        {hasExistingMedia && (
          <Button
            size="small"
            variant="text"
            theme="secondary"
            onClick={handleCancelReplace}
            disabled={isBusy}
          >
            元に戻す
          </Button>
        )}
      </CurrentMediaCard>
    );
  };

  return (
    <Modal
      onClose={onClose}
      size="default"
      title={isNew ? "作品を追加" : "作品を編集"}
      primaryAction={{
        label: "OK",
        onClick: handleSave,
        loading: isSaving,
        // YouTube タブでリンク未入力なら保存できない（作品削除は一覧の
        // ゴミ箱アイコンから行うため、フッターは常にキャンセル + OK）。
        disabled: isBusy || (contentSource === "youtube" && !youtube.trim()),
      }}
      secondaryAction={{ label: "キャンセル", onClick: onClose }}
    >
      <div className="u-col u-gap16 u-w100">
        {/* ===== ① 作品コンテンツ（メディア / YouTube 排他） ===== */}
        <Section>
          <SectionLabel>
            作品コンテンツ
            <span className="label8 u-pl8 u-text-blue">必須</span>
          </SectionLabel>

          {/* マイサイトデザイン（フルスクリーン / バナー）と同じ Tab + c-tab1 構成。
              Tab と c-tab1 を同じ div に入れて gap なしで連結する。 */}
          <div className="u-col u-w100">
            <Tab
              type="tab"
              list={[TAB_MEDIA, TAB_YOUTUBE]}
              activeTab={activeTabLabel}
              onTabChange={handleTabLabelChange}
            />
            <div className="u-col u-w100 u-gap8 c-tab1">
              {contentSource === "media" ? (
                <>
                  {/* 既存メディアの差し替えカード */}
                  {renderCurrentMedia()}

                  {/* 新規アップロード結果（差し替え予定） */}
                  {renderPendingMedia()}

                  {/* ファイル選択（既存を保持しているなら表示しない＝差し替えボタンから入る） */}
                  {(!hasExistingMedia || !keepExistingMedia) &&
                    !pendingMediaKey && (
                      <FileUploader
                        files={mediaSelectedFiles}
                        onChange={setMediaSelectedFiles}
                        multiple={false}
                        accept={MEDIA_ACCEPT}
                        maxSize={MAX_MEDIA_SIZE}
                        height={120}
                        placeholder="音源 / 動画 / 画像 / PDF を 1 ファイル"
                        dragActiveText="ここにドロップ"
                        onUpload={handleMediaUpload}
                        disabled={isBusy}
                      />
                    )}

                  {/* アップロード進捗（差し替え時の隠し input からの場合も含めて常に出す） */}
                  {mediaUploadingFiles.length > 0 && (
                    <UploadProgressBar
                      files={mediaUploadingFiles}
                      onCancel={abortMediaFile}
                      onRetry={(file) => void handleMediaRetry(file)}
                      compact
                    />
                  )}
                </>
              ) : (
                <Input
                  type="text"
                  maxlength={1000}
                  value={youtube}
                  onChange={(e) => setYoutube(e.target.value)}
                  placeholder="https://youtu.be/..."
                />
              )}
            </div>
          </div>
        </Section>

        {/* ===== ② 作品名（必須） ===== */}
        <Input
          label="作品名"
          option="required"
          type="text"
          maxlength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="例: メインテーマ"
        />

        {/* ===== ③ フィルタータグ（ショット編集と同じく作品名直下・ラベルなし） ===== */}
        {filterTags.length > 0 && (
          <TagSelector
            tags={filterTags}
            selectedIds={filterTagIds}
            onChange={setFilterTagIds}
          />
        )}

        {/* ===== ④ 説明 ===== */}
        <Textarea
          label="説明"
          rows={4}
          maxLength={1000}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="作品の解説・聴きどころなどを記述します。"
        />

        {/* ===== ④' 字幕（音源 / 動画のみ。鑑賞モードのテロップに使う） ===== */}
        {showSubtitleField && (
          <Section>
            <Textarea
              label="字幕"
              rows={5}
              maxLength={20000}
              value={subtitleText}
              onChange={(e) => setSubtitleText(e.target.value)}
              placeholder={"[0:05] 冒頭のセリフ\n[0:12] 続きのセリフ"}
            />
            <span className="bodytext-11 u-text-light-gray u-pl4">
              「[分:秒] テキスト」の行が、鑑賞モードで再生位置に合わせてテロップ表示されます。文字起こしの「全文コピー」をそのまま貼り付けできます。
            </span>
          </Section>
        )}

        {/* ===== ⑤ タグ（GALLERY 検索用ハッシュタグ。ContentTag として編集可能） ===== */}
        <ContentTagInput
          label="タグ"
          value={tags}
          onChange={setTags}
          placeholder="Enter / カンマで確定（例: ピアノ, アンビエント, BGM）"
        />

        {/* サムネ画像（補助） */}
        <Section>
          <SectionLabel>サムネ画像</SectionLabel>
          <div
            className="u-row u-gap16"
            style={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <div
              style={{
                width: 96,
                height: 96,
                background: "var(--soremo-background)",
                border: "1px solid var(--soremo-border)",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              {displayImageUrl ? (
                <MediaPreview
                  src={displayImageUrl}
                  mediaType="image"
                  sizeMode={{
                    mode: "fixed-height",
                    height: "96px",
                    maxWidth: "96px",
                  }}
                  disableHoverEffect
                />
              ) : (
                <div className="u-col-center u-w100" style={{ height: "100%" }}>
                  <span
                    className="material-symbols-rounded u-text-light-gray"
                    style={{ fontSize: 40 }}
                  >
                    image
                  </span>
                </div>
              )}
            </div>
            <div className="u-row u-gap8" style={{ alignItems: "center" }}>
              <Button
                size="small"
                variant="outlined"
                theme="secondary"
                onClick={() => imageInputRef.current?.click()}
                disabled={isBusy}
                leftIcon="upload"
              >
                {displayImageUrl ? "差し替え" : "画像を選択"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                theme="primary"
                onClick={() => setShowAiImageModal(true)}
                disabled={isBusy}
                leftIcon="auto_awesome"
              >
                AIで生成
              </Button>
              {displayImageUrl && (
                <IconButton
                  icon="delete"
                  size={18}
                  onClick={handleClearImage}
                  ariaLabel="サムネ画像を削除"
                  disabled={isBusy}
                />
              )}
              {isUploadingImage && (
                <span className="bodytext-11 u-text-light-gray">
                  アップロード中...
                </span>
              )}
            </div>
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageInputChange}
            style={{ display: "none" }}
          />
        </Section>
      </div>

      {/* AI 画像生成モーダル */}
      {showAiImageModal && (
        <AiImageGeneratorModal
          scene="image_profile_work"
          workTitle={title.trim() || undefined}
          workTags={tags.length > 0 ? tags : undefined}
          workDesc={desc.trim() || undefined}
          workId={work ? String(work.id) : undefined}
          onSelect={handleAiImageSelected}
          onClose={() => setShowAiImageModal(false)}
        />
      )}

      {/* 文字起こしモーダル（fileId = AlbumVariation.pk。プレイヤーが無いためシークなし） */}
      {showTranscriptionModal && existingMedia[0]?.id != null && (
        <MediaTranscriptionModal
          fileId={String(existingMedia[0].id)}
          fileName={existingMedia[0].file_name}
          onClose={() => setShowTranscriptionModal(false)}
        />
      )}
    </Modal>
  );
};

export default WorkEditModal;
